import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useFormikContext } from 'formik';
import type {
  CyberSourceFlexConstructor,
  CyberSourceMicroform,
  ExtendedFormikInputProps,
  PaymentFormValues,
} from 'Src/types/payment';
import { castAs } from 'Src/types/cast-helpers';
import { classnames } from 'Utils/classnames/classnames.utils';
import { t } from 'Src/i18n';
import { Title } from 'Components/frameworks/title/Title';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { HostedField } from 'Components/widgets/payment-info/payment-form/HostedField';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { useDispatch } from 'react-redux';
import { endLoading, startLoading } from 'Store/global-loader/globalLoader.action';
import { decode } from 'jsonwebtoken';
import { PaymentFormikInput } from 'Components/widgets/payment-info/paymentFormik';
import { creditCardExpiryValidator } from './paymentProvidersUtils';
const CYBERSOURCE_FIELD_CARD_NUMBER = 'cybersource-card-number';
const CYBERSOURCE_FIELD_EXPIRY_DATE = 'cybersource-card-expiry';
const CYBERSOURCE_FIELD_CVC = 'cybersource-card-cvc';
type Props = {
  token: string | PaymentProviderToken;
};

const toFlexJwt = (token: string | PaymentProviderToken): string =>
  typeof token === 'string' ? token : String(token.value);
const defaultHostedFieldState = {
  valid: false,
  dirty: false,
  empty: true,
};
export const CyberSourceForm = ({ token }: Props) => {
  const formik = useFormikContext<PaymentFormValues>();
  const dispatch = useDispatch();
  const {
    ctx: [
      {
        data: { clientLibrary: scriptUrl, clientLibraryIntegrity: scriptIntegrity },
      },
    ],
  } = decode(toFlexJwt(token)) || {
    ctx: [
      {
        data: {},
      },
    ],
  };
  const { scriptLoaded } = useScriptLoader(scriptUrl, {
    integrity: scriptIntegrity,
    crossorigin: 'anonymous',
  });
  const { isOpen: isFormSubmitted, onOpen: setIsFormSubmitted } = useDisclosure();
  const [creditCardFieldsValidation, setCreditCardFieldsValidation] = useState({
    number: defaultHostedFieldState,
    securityCode: defaultHostedFieldState,
  });
  useEffect(() => {
    formik.isSubmitting && setIsFormSubmitted();
  }, [formik.isSubmitting]);
  const { number: cardNumberValidation, securityCode: securityCodeValidation } =
    creditCardFieldsValidation;
  type HostedFieldDisplayState = {
    hasError: boolean;
    errorMessage: string;
    isValid: boolean;
  };
  const hostedFieldsState = Object.keys(creditCardFieldsValidation).reduce<
    Record<'number' | 'securityCode', HostedFieldDisplayState>
  >(
    (acc, key) => {
      const {
        dirty: isDirty,
        valid: isValid,
        empty: isEmpty,
      } = creditCardFieldsValidation[key as keyof typeof creditCardFieldsValidation];
      const isTouched = isDirty || isFormSubmitted;
      acc[key as 'number' | 'securityCode'] = {
        hasError: isTouched && (!isValid || isEmpty),
        errorMessage: isEmpty
          ? t('common.form.errors.requiredField')
          : t('common.form.errors.invalidFieldFormat'),
        isValid: isDirty && isValid,
      };
      return acc;
    },
    {} as Record<'number' | 'securityCode', HostedFieldDisplayState>,
  );
  const microformInstanceRef = useRef<CyberSourceMicroform | null>(null);
  useEffect(() => {
    if (scriptLoaded && token) {
      const options = {
        keyId: token,
        keystore: token,
        container: `#${CYBERSOURCE_FIELD_CARD_NUMBER}`,
        label: '.HostedField-label',
        placeholder: t('common.payment.hipay.placeholderCardNumber'),
        styles: {
          input: {
            'font-size': '14px',
            color: '#000',
          },
          '::placeholder': {
            color: '#9ca3af',
          },
        },
      };
      const form = new (castAs<CyberSourceFlexConstructor>(window.Flex))(toFlexJwt(token));
      const microform = form.microform(options);
      microformInstanceRef.current = microform;
      [
        {
          type: 'number',
          placeholder: t('common.payment.hipay.placeholderCardNumber'),
          id: CYBERSOURCE_FIELD_CARD_NUMBER,
        },
        {
          type: 'securityCode',
          placeholder: t('common.payment.hipay.placeholderCvc'),
          id: CYBERSOURCE_FIELD_CVC,
        },
      ].map(({ type, placeholder, id }) => {
        const field = microform.createField(type, {
          placeholder,
        });
        field.load(`#${id}`);
        field.on('change', ({ valid, card, empty }) => {
          if (card?.[0]?.name) {
            formik.setFieldValue('paymentProduct', card[0]?.name);
          }

          setCreditCardFieldsValidation((validation) => ({
            ...validation,
            [type]: {
              valid,
              empty,
              dirty: true,
            },
          }));
        });
      });
    }
  }, [scriptLoaded]);
  const { token: tokenError, ...errors } = formik.errors;
  const isFormValid = !Object.keys(errors).length;
  const shouldRequestToken =
    microformInstanceRef.current &&
    cardNumberValidation.valid &&
    securityCodeValidation.valid &&
    !formik.errors?.[CYBERSOURCE_FIELD_EXPIRY_DATE] &&
    !formik.values.token &&
    isFormValid &&
    formik.isSubmitting;
  useEffect(() => {
    if (shouldRequestToken) {
      dispatch(startLoading());
      const creditCardExpiryDateObject = new Date(formik.values[CYBERSOURCE_FIELD_EXPIRY_DATE]);
      const expirationMonth = (creditCardExpiryDateObject.getMonth() + 1)
        .toString()
        .padStart(2, '0');
      const expirationYear = creditCardExpiryDateObject.getFullYear().toString();
      microformInstanceRef.current!.createToken(
        {
          expirationMonth,
          expirationYear,
        },
        (err, tokenizedCard) => {
          if (err) {
            dispatch(endLoading());
            throw Error(err.message || 'Could not generate token');
          }

          formik.setFieldValue('token', tokenizedCard);
          setTimeout(formik.submitForm, 0);
        },
      );
    }

    /**
     * If we have a token and the form payment process is on error, we must delete the old token
     * because the user could update his credit card data, so we must request a new token on the next submit
     */
    const shouldDeleteToken =
      formik.values.token && formik.status && formik.status.paymentProcessOnError;

    if (shouldDeleteToken) {
      formik.setFieldValue('token', '');
    }
  }, [formik.isSubmitting, formik.status]);
  const inputStyle =
    'outline-none rounded-2 text-14 text-black duration-250 ease-in transition-all w-full';
  const [monthExpiryPlaceholder, yearExpiryPlaceholder] = t(
    'common.payment.hipay.placeholderExpiryDate',
  )
    .toUpperCase()
    .split('/');
  return (
    <Fragment>
      <PaymentFormikInput type="hidden" isRequired name="token" />
      <Title
        className={classnames('mb-10', {
          'formik-errored': !formik.values.token,
        })}
        theme="title-brand-n2"
      >
        {t('common.payment.hipay.cardDetailTitle')}
      </Title>
      <Capsule header="none">
        <div className="flex flex-wrap px-0 py-15">
          <HostedField
            label={t('common.payment.hipay.cardNumberLabel')}
            id={`${CYBERSOURCE_FIELD_CARD_NUMBER}`}
            {...hostedFieldsState.number}
          />
          <div className="flex w-full flex-col sm:flex-row">
            <HostedField
              label={t('common.payment.hipay.expiryDateLabel')}
              id={`${CYBERSOURCE_FIELD_EXPIRY_DATE}`}
              small
              hasError={formik.errors?.[CYBERSOURCE_FIELD_EXPIRY_DATE]}
              isValid={
                formik.touched?.[CYBERSOURCE_FIELD_EXPIRY_DATE] &&
                !formik.errors?.[CYBERSOURCE_FIELD_EXPIRY_DATE]
              }
              container={false}
            >
              <PaymentFormikInput
                {...castAs<ExtendedFormikInputProps>({
                  type: 'creditCardExpiry',
                  name: `${CYBERSOURCE_FIELD_EXPIRY_DATE}`,
                  className: 'w-full',
                  dateFormat: t('booking.form.dateFormat'),
                  monthPlaceholder: monthExpiryPlaceholder,
                  yearPlaceholder: yearExpiryPlaceholder,
                  validate: creditCardExpiryValidator,
                  customClasses: inputStyle,
                })}
              />
            </HostedField>
            <HostedField
              label={t('common.payment.hipay.cvcLabel')}
              id={`${CYBERSOURCE_FIELD_CVC}`}
              small
              {...hostedFieldsState.securityCode}
            />
          </div>
        </div>
      </Capsule>
    </Fragment>
  );
};
